import { startKlaviyoLanguageSync } from "@lyle/weglot";

const KLAVIYO_REVISION = "2026-04-15";

class LyleSiteFooter extends HTMLElement {
  #abortController = null;

  connectedCallback() {
    this.#abortController = new AbortController();

    this.querySelectorAll("[data-newsletter-form]").forEach((form) => {
      this.#bindNewsletterForm(form);
    });

    this.#bindCookieSettings();
    startKlaviyoLanguageSync();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  #bindNewsletterForm(form) {
    const email = form.querySelector("[data-newsletter-email]");

    form.addEventListener("submit", this.#handleNewsletterSubmit, {
      signal: this.#abortController.signal,
    });

    email?.addEventListener("input", () => this.#resetNewsletterState(form), {
      signal: this.#abortController.signal,
    });
  }

  #handleNewsletterSubmit = async (event) => {
    event.preventDefault();

    const form = event.currentTarget;
    const email = form.querySelector("[data-newsletter-email]");

    if (!email?.validity.valid) {
      this.#setNewsletterState(form, "error");
      return;
    }

    this.#setNewsletterState(form, "loading");

    try {
      const formData = new FormData(form);
      const response = await window.fetch(form.action, {
        body: JSON.stringify({
          data: {
            type: "subscription",
            attributes: {
              custom_source: "footer",
              profile: {
                data: {
                  type: "profile",
                  attributes: {
                    email: formData.get("email"),
                    subscriptions: {
                      email: {
                        marketing: {
                          consent: "SUBSCRIBED",
                        },
                      },
                    },
                  },
                },
              },
            },
            relationships: {
              list: {
                data: {
                  type: "list",
                  id: formData.get("g"),
                },
              },
            },
          },
        }),
        headers: {
          "content-type": "application/vnd.api+json",
          revision: KLAVIYO_REVISION,
        },
        method: form.method,
      });

      if (response.status === 202) {
        this.#setNewsletterState(form, "success");
        return;
      }

      this.#setNewsletterState(form, "error");
    } catch {
      this.#setNewsletterState(form, "error");
    }
  };

  #resetNewsletterState(form) {
    if (form.dataset.state === "idle") return;

    this.#setNewsletterState(form, "idle");
  }

  #setNewsletterState(form, state) {
    const submit = form.querySelector("[data-newsletter-submit]");
    const tick = form.querySelector("[data-newsletter-tick]");
    const error = form.querySelector("[data-newsletter-error]");
    const title = form.querySelector("[data-newsletter-title]");
    const subtitle = form.querySelector("[data-newsletter-subtitle]");
    const isLoading = state === "loading";
    const isSuccess = state === "success";
    const isError = state === "error";

    form.dataset.state = state;
    form.classList.remove("is-loading", "is-success", "is-error");

    if (state !== "idle") {
      form.classList.add(`is-${state}`);
    }

    if (submit) {
      submit.disabled = isLoading;
      submit.hidden = isSuccess;
    }

    if (tick) {
      tick.hidden = !isSuccess;
    }

    if (error) {
      error.hidden = !isError;
    }

    if (title) {
      const titleText = isSuccess
        ? form.dataset.successTitle
        : title.dataset.defaultText;
      title.textContent = titleText;
      title.hidden = !titleText;
    }

    if (subtitle) {
      const subtitleText = isSuccess
        ? form.dataset.successSubtitle
        : subtitle.dataset.defaultText;
      subtitle.textContent = subtitleText;
      subtitle.hidden = !subtitleText;
    }
  }

  #bindCookieSettings() {
    const button = this.querySelector("[data-cookie-settings]");

    if (!button) return;

    button.addEventListener(
      "click",
      () => {
        window.lyleOneTrustBannerOpen = true;
        window.dispatchEvent(new CustomEvent("onetrust:opened"));
        window.OneTrust?.ToggleInfoDisplay?.();
      },
      {
        signal: this.#abortController.signal,
      },
    );

    if (window.lyleOneTrustBannerOpen === false) {
      button.hidden = false;
    }

    window.addEventListener(
      "onetrust:closed",
      () => {
        button.hidden = false;
      },
      { signal: this.#abortController.signal },
    );
  }
}

if (!customElements.get("lyle-site-footer")) {
  customElements.define("lyle-site-footer", LyleSiteFooter);
}
